import { HarmonyTableColumn, HarmonyTableRow } from '../../Components/Workspace/Generic/Table';

/**
 * Return a set of rows supported by Enablers Data Grid
 * @param columns array of Harmony table columns type
 * @param rowsData Array of rows (array of strings) meant to be mapped to the columns provided
 * @returns array of harmony table rows
 * */
export const getRowsForDataGrid = (columns: HarmonyTableColumn[], rowsData: string[][]): HarmonyTableRow[] => {
  return rowsData.length > 0
    ? rowsData.map((data: string[], index: number) => {
        const row = {
          id: index,
          cells: columns.reduce((cell, column, index) => {
            (cell as any)[column.field] = `${data[index]}`;
            return cell;
          }, {}),
        };
        return row;
      })
    : [];
};

/**
 * Return a set of rows supported by Enablers Data Grid and the htmlRows if needed
 * @param array array of type T data to map
 * @param getHarmonyRow function that receives an element of type  T and the current index of it and returns a HarmonyTableRow.
 * @param getHTMLElement function that receives an element of type  T and the current index of it and returns a JSX.Element.
 * @returns an object with <data> and <html> properties
 * */
export const getRowsDataAndHTML = <T>(
  array: T[],
  getHarmonyRow: (data: T, index: number) => HarmonyTableRow,
  getHTMLElement?: (data: T, index: number) => JSX.Element
) => {
  const htmlRows: JSX.Element[] = [];
  const rows: HarmonyTableRow[] = [];

  for (let index = 0; index < array.length; index++) {
    const data = array[index];
    rows.push(getHarmonyRow(data, index));
    if (getHTMLElement) htmlRows.push(getHTMLElement(data, index));
  }

  return {
    data: rows,
    html: htmlRows,
  };
};

/**
 * Return a component based on the loading param
 * @param loading show transient if true or when ready if false
 * @param transientComponent
 * @param componentWhenReady
 * @returns the expected componenet based on the loading clause
 * */
export const getComponentWhenReady = (
  loading: boolean,
  transientComponent: JSX.Element,
  componentWhenReady: JSX.Element
) => {
  return loading ? transientComponent : componentWhenReady;
};

/**
 *
 * @param numberOfRows (optional) The number of loading rows that will show up
 * @returns A list of empty rows with empty cells, lenght is numberOfRows. 10 by default
 */
export function getDummyRows(numberOfRows?: number) {
  const dummyRows = [];
  const pageSize = numberOfRows ? numberOfRows : 10;
  for (let i = 0; i < pageSize; i++) {
    dummyRows.push({ id: i, cells: {} });
  }
  return dummyRows;
}

/**
 * Default comparison function for data grid
 * @param a harmony table row
 * @param b harmony table row
 * @param event which trigger the action to compare
 * @returns which row is greater than the other
 * */
export const defaultComparison = (a: HarmonyTableRow, b: HarmonyTableRow, event: any) => {
  const fieldA = a.cells[event.detail.sortBy];
  const fieldB = b.cells[event.detail.sortBy];
  const isAscending = event.detail.isAscending;

  if (fieldA > fieldB) {
    return isAscending ? 1 : -1;
  }
  if (fieldA < fieldB) {
    return isAscending ? -1 : 1;
  }
  return 0;
};
